import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import dayjs from "dayjs";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import OutlineCard from "../../../../../components/OutlineCard";
import Text from "../../../../../components/Text";
import {
  FAMILY_HISTORY_CONDITIONS,
  MEDICAL_CONDITIONS,
  MEDICAL_CONDITIONS_STRING,
} from "./new-intake-form-contant/intake-constant";

interface IntakeMedicalHistoryProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
}

const IntakeMedicalHistory = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched: _touched,
  errors: _errors,
  viewOnly,
}: IntakeMedicalHistoryProps) => {
  const allMedicalConditionsSelected = MEDICAL_CONDITIONS.every(
    (condition) => !!values.medicalHistory?.[condition.value]
  );

  const handleAddFamilyHistory = () => {
    const currentHistory = values.familyHistory || [];
    setFieldValue("familyHistory", [
      ...currentHistory,
      { problem: "", relative: "", age: "" },
    ]);
  };

  const handleRemoveFamilyHistory = (index: number) => {
    const currentHistory = values.familyHistory || [];
    const newHistory = currentHistory.filter(
      (_: FamilyHistoryItem, i: number) => i !== index
    );
    setFieldValue("familyHistory", newHistory);
  };

  const handleAddSurgicalHistory = () => {
    const currentHistory = values.surgicalHistory || [];
    setFieldValue("surgicalHistory", [
      ...currentHistory,
      { type: "", date: "", doctor: "", location: "" },
    ]);
  };

  const handleRemoveSurgicalHistory = (index: number) => {
    const currentHistory = values.surgicalHistory || [];
    const newHistory = currentHistory.filter(
      (_: SurgicalHistoryItem, i: number) => i !== index
    );
    setFieldValue("surgicalHistory", newHistory);
  };

  const handleAddMedication = () => {
    const currentMedications = values.medications || [];
    setFieldValue("medications", [
      ...currentMedications,
      { name: "", dosage: "", frequency: "", prescribedBy: "" },
    ]);
  };

  const handleRemoveMedication = (index: number) => {
    const currentMedications = values.medications || [];
    const newMedications = currentMedications.filter(
      (_: MedicationItem, i: number) => i !== index
    );
    setFieldValue("medications", newMedications);
  };

  const handleSelectAll = () => {
    const checked = !allMedicalConditionsSelected;

    const newMedicalHistory = MEDICAL_CONDITIONS.reduce(
      (acc, condition) => {
        acc[condition.value] = checked;
        return acc;
      },
      {} as Record<string, boolean>
    );

    setFieldValue("medicalHistory", newMedicalHistory);
  };

  return (
    <Flex gap={0} flexDirection={"column"}>
      {/* <Divider textAlign="left">
        <Text>PATIENT MEDICAL HISTORY</Text>
      </Divider> */}

      {/* Allergies Section */}
      <OutlineCard>
        <Text variant="subtitle1" fontWeight="bold">
          Allergies
        </Text>
        <Grid container spacing={0}>
          {ALLERGIES.map((allergy) => (
            <Grid item xs={12} sm={6} md={3} key={allergy.value}>
              <FormControlLabel
                disabled={viewOnly}
                control={
                  <Checkbox
                    disabled={viewOnly}
                    checked={values.allergies?.[allergy.value] || false}
                    onChange={(e) => {
                      setFieldValue(
                        `allergies.${allergy.value}`,
                        e.target.checked
                      );
                    }}
                    name={`allergies.${allergy.value}`}
                  />
                }
                label={allergy.label}
              />
            </Grid>
          ))}
        </Grid>
        <CustomTextArea
          disabled={viewOnly}
          label="Other"
          sx={{ mt: 1 }}
          rows={3}
          variant="outlined"
          name="allergiesNote"
          placeholder="Enter Allergy History"
          onBlur={handleBlur}
          value={values?.allergiesNote}
          onChange={handleChange}
        />
      </OutlineCard>

      {/* Family History Section */}
      <OutlineCard sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="bold" mb={1}>
          Family History
        </Text>
        {(values.familyHistory || []).map(
          (item: FamilyHistoryItem, index: number) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={12} md={4}>
                <Box width="100%">
                  <CustomSelect
                    readOnly={viewOnly}
                    label="Problem Name"
                    value={item.problem}
                    onChange={(value) => {
                      const newHistory = (values.familyHistory || []).map(
                        (h: FamilyHistoryItem, i: number) =>
                          i === index ? { ...h, problem: value } : h
                      );
                      setFieldValue("familyHistory", newHistory);
                    }}
                    options={PROBLEM_OPTIONS}
                    clearable
                    placeholder="Select Problem"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box width="100%">
                  <CustomSelect
                    readOnly={viewOnly}
                    label="Relative"
                    value={item.relative}
                    onChange={(value) => {
                      const newHistory = (values.familyHistory || []).map(
                        (h: FamilyHistoryItem, i: number) =>
                          i === index ? { ...h, relative: value } : h
                      );
                      setFieldValue("familyHistory", newHistory);
                    }}
                    options={RELATIVE_OPTIONS}
                    clearable
                    placeholder="Select Relative"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box width="100%">
                  <Input
                    label="Age"
                    textFieldProps={{
                      type: "number",
                      value: item.age || "",
                      onChange: (e) => {
                        const newHistory = (values.familyHistory || []).map(
                          (h: FamilyHistoryItem, i: number) =>
                            i === index ? { ...h, age: e.target.value } : h
                        );
                        setFieldValue("familyHistory", newHistory);
                      },
                      placeholder: "Enter Age",
                      disabled: viewOnly,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  height="100%"
                  justifyContent="center"
                >
                  <DeleteOutlineOutlinedIcon
                    sx={{
                      cursor: "pointer",
                      marginX: 2,
                    }}
                    onClick={() =>
                      viewOnly ? null : handleRemoveFamilyHistory(index)
                    }
                    color="error"
                  />
                </Box>
              </Grid>
            </Grid>
          )
        )}
        {!viewOnly && (
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddFamilyHistory}
            variant="outlined"
          >
            Add More
          </Button>
        )}
      </OutlineCard>

      {/* Social History Section */}
      <OutlineCard sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="bold">
          Social History
        </Text>
        <Grid container spacing={0}>
          {SOCIAL_HISTORY_QUESTIONS.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.name}>
              <FormControlLabel
                disabled={viewOnly}
                control={
                  <Checkbox
                    disabled={viewOnly}
                    checked={values.socialHistory?.[item.name] || false}
                    onChange={(e) => {
                      setFieldValue(
                        `socialHistory.${item.name}`,
                        e.target.checked
                      );
                    }}
                    name={`socialHistory.${item.name}`}
                  />
                }
                label={item.question}
              />
            </Grid>
          ))}
        </Grid>

        <CustomTextArea
          disabled={viewOnly}
          sx={{ mt: 1 }}
          label="Other"
          rows={2}
          variant="outlined"
          name="socialHistoryNote"
          placeholder="Enter Social History"
          onBlur={handleBlur}
          value={values?.socialHistoryNote}
          onChange={handleChange}
        />
      </OutlineCard>

      {/* Surgical History Section */}
      <OutlineCard sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="bold" mb={1}>
          Surgical History
        </Text>
        {(values.surgicalHistory || []).map(
          (item: SurgicalHistoryItem, index: number) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={12} md={3}>
                <Box width="100%">
                  <Input
                    label="Type of Surgery"
                    textFieldProps={{
                      value: item.type || "",
                      onChange: (e) => {
                        const newHistory = (values.surgicalHistory || []).map(
                          (h: SurgicalHistoryItem, i: number) =>
                            i === index ? { ...h, type: e.target.value } : h
                        );
                        setFieldValue("surgicalHistory", newHistory);
                      },
                      placeholder: "Enter Type of Surgery",
                      disabled: viewOnly,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CustomDatePicker
                  disabled={viewOnly}
                  label="Year or Date"
                  placeholder="Enter Year or Date"
                  value={item.date ? dayjs(item.date) : null}
                  onChange={(value) => {
                    const newHistory = (values.surgicalHistory || []).map(
                      (h: SurgicalHistoryItem, i: number) =>
                        i === index ? { ...h, date: value } : h
                    );
                    setFieldValue("surgicalHistory", newHistory);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box width="100%">
                  <Input
                    label="Doctor"
                    textFieldProps={{
                      value: item.doctor || "",
                      onChange: (e) => {
                        const newHistory = (values.surgicalHistory || []).map(
                          (h: SurgicalHistoryItem, i: number) =>
                            i === index ? { ...h, doctor: e.target.value } : h
                        );
                        setFieldValue("surgicalHistory", newHistory);
                      },
                      placeholder: "Enter Doctor's Name",
                      disabled: viewOnly,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box width="100%">
                  <Input
                    label="Location"
                    textFieldProps={{
                      value: item.location || "",
                      onChange: (e) => {
                        const newHistory = (values.surgicalHistory || []).map(
                          (h: SurgicalHistoryItem, i: number) =>
                            i === index ? { ...h, location: e.target.value } : h
                        );
                        setFieldValue("surgicalHistory", newHistory);
                      },
                      placeholder: "Enter Location",
                      disabled: viewOnly,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Box
                  display="flex"
                  alignItems="center"
                  height="100%"
                  justifyContent="center"
                >
                  <DeleteOutlineOutlinedIcon
                    sx={{
                      cursor: "pointer",
                      marginX: 2,
                    }}
                    onClick={() =>
                      viewOnly ? null : handleRemoveSurgicalHistory(index)
                    }
                    color="error"
                  />
                </Box>
              </Grid>
            </Grid>
          )
        )}
        {!viewOnly && (
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddSurgicalHistory}
            variant="outlined"
          >
            Add More
          </Button>
        )}
      </OutlineCard>

      {/* Medical History Section */}
      <OutlineCard sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="bold">
          Medical History
        </Text>
        <Grid container spacing={0}>
          {!viewOnly && (
            <Grid item xs={12}>
              <Button
                disabled={viewOnly}
                variant="contained"
                sx={{ my: 1 }}
                onClick={handleSelectAll}
              >
                {allMedicalConditionsSelected ? "Deselect All" : "Select All"}
              </Button>
            </Grid>
          )}
          {MEDICAL_CONDITIONS.map((condition) => (
            <Grid item xs={12} sm={6} md={4} key={condition.value}>
              <FormControlLabel
                disabled={viewOnly}
                control={
                  <Checkbox
                    disabled={viewOnly}
                    name={`medicalHistory.${condition.value}`}
                    checked={!!values.medicalHistory?.[condition.value]}
                    onChange={(e) => {
                      setFieldValue(
                        `medicalHistory.${condition.value}`,
                        e.target.checked
                      );
                    }}
                  />
                }
                label={condition.label}
              />
            </Grid>
          ))}
        </Grid>

        <CustomTextArea
          disabled={viewOnly}
          label="Other"
          sx={{ mt: 1 }}
          rows={3}
          variant="outlined"
          name="medicalHistoryNote"
          placeholder="Enter Medical History"
          onBlur={handleBlur}
          value={values?.medicalHistoryNote}
          onChange={handleChange}
        />
      </OutlineCard>

      {/* Medications Section */}
      <OutlineCard sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="bold" mb={1}>
          Medications
        </Text>
        {(values.medications || []).map(
          (item: MedicationItem, index: number) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={12} md={3}>
                <Box width="100%">
                  <Input
                    label="Medication"
                    textFieldProps={{
                      value: item.name || "",
                      onChange: (e) => {
                        const newMedications = (values.medications || []).map(
                          (m: MedicationItem, i: number) =>
                            i === index ? { ...m, name: e.target.value } : m
                        );
                        setFieldValue("medications", newMedications);
                      },
                      placeholder: "Enter Medication",
                      disabled: viewOnly,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box width="100%">
                  <CustomSelect
                    readOnly={viewOnly}
                    label="Dosage"
                    value={item.dosage}
                    onChange={(value) => {
                      const newMedications = (values.medications || []).map(
                        (m: MedicationItem, i: number) =>
                          i === index ? { ...m, dosage: value } : m
                      );
                      setFieldValue("medications", newMedications);
                    }}
                    options={DOSAGE_OPTIONS}
                    clearable
                    placeholder="Select Dosage"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box width="100%">
                  <CustomSelect
                    readOnly={viewOnly}
                    label="Frequency"
                    value={item.frequency}
                    onChange={(value) => {
                      const newMedications = (values.medications || []).map(
                        (m: MedicationItem, i: number) =>
                          i === index ? { ...m, frequency: value } : m
                      );
                      setFieldValue("medications", newMedications);
                    }}
                    options={FREQUENCY_OPTIONS}
                    clearable
                    placeholder="Select Frequency"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Input
                  label="Prescribed By"
                  textFieldProps={{
                    value: item.prescribedBy || "",
                    onChange: (e) => {
                      const newMedications = (values.medications || []).map(
                        (m: MedicationItem, i: number) =>
                          i === index
                            ? { ...m, prescribedBy: e.target.value }
                            : m
                      );
                      setFieldValue("medications", newMedications);
                    },
                    placeholder: "Enter Prescribed By",
                    disabled: viewOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Box
                  display="flex"
                  alignItems="center"
                  height="100%"
                  justifyContent="center"
                >
                  <DeleteOutlineOutlinedIcon
                    sx={{
                      cursor: "pointer",
                      marginX: 2,
                    }}
                    onClick={() =>
                      viewOnly ? null : handleRemoveMedication(index)
                    }
                    color="error"
                  />
                </Box>
              </Grid>
            </Grid>
          )
        )}
        {!viewOnly && (
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddMedication}
            variant="outlined"
          >
            Add More
          </Button>
        )}
      </OutlineCard>
    </Flex>
  );
};

export default IntakeMedicalHistory;

// constants
const ALLERGIES = [
  { label: "NONE/Known Allergies", value: "none" },
  { label: "Adhesive Tape", value: "adhesiveTape" },
  { label: "Anesthesia", value: "anesthesia" },
  { label: "Aspirin", value: "aspirin" },
  { label: "Codeine", value: "codeine" },
  { label: "Dairy Products", value: "dairyProducts" },
  { label: "Iodine/Shellfish/Contrast", value: "iodine" },
  { label: "Latex", value: "latex" },
  { label: "Morphine", value: "morphine" },
  { label: "Penicillin", value: "penicillin" },
];

const SOCIAL_HISTORY_QUESTIONS = [
  { question: "Do you drink alcohol?", name: "alcohol" },
  { question: "Do you smoke?", name: "smoke" },
  { question: "Do you drink caffeine?", name: "caffeine" },
  { question: "Are you sexually active?", name: "sexuallyActive" },
  { question: "Do you wish to be checked for STDs?", name: "stdCheck" },
];

const RELATIVES = ["Mother", "Father", "Sibling", "Grandparent", "Other"];

const RELATIVE_OPTIONS = RELATIVES.map((relative) => ({
  label: relative,
  value: relative,
}));

const PROBLEM_OPTIONS = [
  ...FAMILY_HISTORY_CONDITIONS,
  ...MEDICAL_CONDITIONS_STRING,
];

const DOSAGE_OPTIONS = [
  { label: "1 mg", value: "1mg" },
  { label: "2 mg", value: "2mg" },
  { label: "5 mg", value: "5mg" },
  { label: "10 mg", value: "10mg" },
  { label: "20 mg", value: "20mg" },
  { label: "25 mg", value: "25mg" },
  { label: "50 mg", value: "50mg" },
  { label: "100 mg", value: "100mg" },
  { label: "250 mg", value: "250mg" },
  { label: "500 mg", value: "500mg" },
  { label: "1000 mg", value: "1000mg" },
];

const FREQUENCY_OPTIONS = [
  { label: "Once daily", value: "Once daily" },
  { label: "Twice daily", value: "Twice daily" },
  { label: "Three times daily", value: "Three times daily" },
  { label: "Four times daily", value: "Four times daily" },
  { label: "As needed", value: "As needed" },
  { label: "Every 4 hours", value: "Every 4 hours" },
  { label: "Every 6 hours", value: "Every 6 hours" },
  { label: "Every 8 hours", value: "Every 8 hours" },
  { label: "Every 12 hours", value: "Every 12 hours" },
  { label: "Every 24 hours", value: "Every 24 hours" },
];

interface FamilyHistoryItem {
  problem: string;
  relative: string;
  age: number;
}

interface SurgicalHistoryItem {
  type: string;
  date: string;
  doctor: string;
  location: string;
}

interface MedicationItem {
  name: string;
  dosage: string;
  frequency: string;
  prescribedBy: string;
}
